import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  branchesApi,
  branchesQueryKeys,
  type BranchInput,
} from "../../shared/api/branches";
import { apiError } from "../../shared/api/client";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { AddressPicker } from "../../shared/ui/AddressPicker";
import { useGeoNames } from "../../shared/api/geo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Info } from "lucide-react";

/** Cài đặt — Chi nhánh. List + tạo chi nhánh (Pro). Lite chỉ 1 chi nhánh → tạo thêm nhận 402. */
export function BranchesSettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: branchesQueryKeys.all(),
    queryFn: () => branchesApi.list(),
  });
  const { provinceName } = useGeoNames();

  const [form, setForm] = useState<BranchInput>({ code: "", name: "" });
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState(false);
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => branchesApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: branchesQueryKeys.all() });
      setMsg("Đã tạo chi nhánh.");
      setError(null);
      setUpgrade(false);
      setForm({ code: "", name: "" });
    },
    onError: (e: unknown) => {
      const err = apiError(e);
      setMsg(null);
      if (err.status === 402) {
        setUpgrade(true);
        setError(null);
      } else {
        setError(err.detail);
        setUpgrade(false);
      }
    },
  });

  const archive = useMutation({
    mutationFn: (id: string) => branchesApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: branchesQueryKeys.all() }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => branchesApi.remove(id),
    onSuccess: () => {
      setBulkError(null);
      qc.invalidateQueries({ queryKey: branchesQueryKeys.all() });
    },
    onError: (e: unknown) => setBulkError(apiError(e).detail),
  });

  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete(
      [...sel.selectedIds],
      branchesApi.remove
    );
    setBulkPending(false);
    sel.clear();
    qc.invalidateQueries({ queryKey: branchesQueryKeys.all() });
    setBulkError(
      failed.length
        ? `${failed.length} chi nhánh không xoá được (đang được dùng).`
        : null
    );
  }

  const branches = q.data ?? [];

  return (
    <div className="font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Chi nhánh
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Quản lý các chi nhánh của phòng khám. Nhiều chi nhánh là tính năng gói Pro.
        </p>
      </div>

      <div className="grid max-w-3xl gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Danh sách chi nhánh</CardTitle>
          </CardHeader>
          <CardContent>
            {q.isLoading ? (
              <p className="text-sm text-muted-foreground">Đang tải…</p>
            ) : branches.length === 0 ? (
              <p className="text-sm text-muted-foreground">Chưa có chi nhánh.</p>
            ) : (
              <>
                {bulkError && (
                  <p className="mb-2 text-sm text-destructive">{bulkError}</p>
                )}
                <BulkDeleteBar
                  count={sel.count}
                  noun="chi nhánh"
                  pending={bulkPending}
                  onClear={sel.clear}
                  onDelete={bulkDelete}
                />
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-9 text-center">
                          <input
                            type="checkbox"
                            aria-label="Chọn tất cả"
                            className="h-4 w-4 accent-primary align-middle"
                            checked={
                              branches.length > 0 &&
                              branches.every((b) => sel.selectedIds.has(b.id))
                            }
                            ref={(el) => {
                              if (el)
                                el.indeterminate =
                                  branches.some((b) =>
                                    sel.selectedIds.has(b.id)
                                  ) &&
                                  !branches.every((b) =>
                                    sel.selectedIds.has(b.id)
                                  );
                            }}
                            onChange={() => {
                              const next = new Set(sel.selectedIds);
                              if (branches.every((b) => next.has(b.id)))
                                branches.forEach((b) => next.delete(b.id));
                              else branches.forEach((b) => next.add(b.id));
                              sel.setSelectedIds(next);
                            }}
                          />
                        </TableHead>
                        <TableHead>Mã</TableHead>
                        <TableHead>Tên</TableHead>
                        <TableHead>Tỉnh/TP</TableHead>
                        <TableHead>Trạng thái</TableHead>
                        <TableHead className="text-right" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {branches.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell className="text-center">
                            <input
                              type="checkbox"
                              aria-label="Chọn dòng"
                              className="h-4 w-4 accent-primary align-middle"
                              checked={sel.selectedIds.has(b.id)}
                              onChange={() => sel.toggle(b.id)}
                            />
                          </TableCell>
                          <TableCell>{b.code}</TableCell>
                          <TableCell>{b.name}</TableCell>
                          <TableCell>
                            {b.provinceCode
                              ? provinceName.get(String(b.provinceCode)) ??
                                b.provinceCode
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {b.status === "ACTIVE" ? (
                              <Badge variant="success">Hoạt động</Badge>
                            ) : (
                              <Badge variant="muted">Đã lưu trữ</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {b.status === "ACTIVE" && branches.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => archive.mutate(b.id)}
                                >
                                  Lưu trữ
                                </Button>
                              )}
                              {branches.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  disabled={remove.isPending}
                                  onClick={() => {
                                    if (
                                      confirm(
                                        `Xoá vĩnh viễn chi nhánh "${b.name}"? Không thể hoàn tác.`
                                      )
                                    )
                                      remove.mutate(b.id);
                                  }}
                                >
                                  Xoá
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Thêm chi nhánh</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="branch-code">Mã chi nhánh</Label>
                <Input
                  id="branch-code"
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, code: e.target.value }))
                  }
                  placeholder="VD: CN2"
                  className="w-36"
                />
              </div>
              <div className="min-w-[220px] flex-1 space-y-1.5">
                <Label htmlFor="branch-name">Tên chi nhánh</Label>
                <Input
                  id="branch-name"
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="VD: Chi nhánh Quận 7"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="branch-address">Địa chỉ</Label>
              <Input
                id="branch-address"
                type="text"
                value={form.address ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, address: e.target.value || null }))
                }
                placeholder="Số nhà, đường, khu phố"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <AddressPicker
                provinceCode={form.provinceCode ?? null}
                wardCode={form.wardCode ?? null}
                disabled={create.isPending}
                onChange={({ provinceCode, wardCode }) =>
                  setForm((f) => ({ ...f, provinceCode, wardCode }))
                }
              />
            </div>

            {upgrade && (
              <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span>
                  Nhiều chi nhánh là tính năng của gói{" "}
                  <strong>Addy HIS Pro</strong>. Vui lòng nâng cấp ở mục{" "}
                  <Link
                    to="/settings/subscription"
                    className="font-medium text-primary hover:underline"
                  >
                    Gói dịch vụ
                  </Link>{" "}
                  để thêm chi nhánh.
                </span>
              </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            {msg && <p className="text-sm text-emerald-600">{msg}</p>}

            <div className="flex justify-end">
              <Button
                type="button"
                disabled={
                  !form.code.trim() || !form.name.trim() || create.isPending
                }
                onClick={() => {
                  setMsg(null);
                  setError(null);
                  setUpgrade(false);
                  create.mutate();
                }}
              >
                {create.isPending ? "Đang tạo…" : "Thêm chi nhánh"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
